import { Extension } from '@tiptap/core'
import { useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { useEffect } from 'react'
import { getTemplateVariableGroupConfig } from '@/lib/template-variable-groups'
import {
  createTemplateVariableMatcher,
  findTemplateVariableTokenAtOffset,
  matchTemplateVariableToken,
  toTemplateVariableSyntax,
} from '@/lib/template-variable-tokens'

export interface RichTextVariableToken {
  syntax: string
  label: string
  parentType?: string
}

type TemplateVariableDecorationStorage = Record<
  string,
  {
    highlightUnresolvedTemplateVariables: boolean
    variableTokens: RichTextVariableToken[]
  }
>

const emptyVariableTokens: RichTextVariableToken[] = []

interface UseRichTextEditorOptions {
  id?: string
  initialContent?: string
  placeholder?: string
  editable?: boolean
  minHeight?: string
  onUpdate?: (html: string) => void
  onFocus?: () => void
  onBlur?: () => void
  variableTokens?: RichTextVariableToken[]
  highlightUnresolvedTemplateVariables?: boolean
  /** Enables the Link mark (`editor.chain().setLink(...)`/`unsetLink()`) for a links toolbar. */
  links?: boolean
  /**
   * Restricts StarterKit to the Google Calendar description HTML subset
   * (`b i u a ul ol li br`) by disabling heading/blockquote/codeBlock/code/
   * horizontalRule/strike at the extension level, so pasted content can't
   * smuggle in a tag the toolbar never offered. Purely additive — omitted or
   * false keeps every existing caller's default StarterKit config unchanged.
   */
  restrictedFormatting?: boolean
}

export const variableTokenPluginKey = new PluginKey('variableTokenDecorations')

export const variableTokenDecorations = Extension.create<{
  highlightUnresolvedTemplateVariables: boolean
  variableTokens: RichTextVariableToken[]
}>({
  name: 'variableTokenDecorations',

  addOptions() {
    return {
      highlightUnresolvedTemplateVariables: false,
      variableTokens: [],
    }
  },

  addStorage() {
    return {
      highlightUnresolvedTemplateVariables: this.options.highlightUnresolvedTemplateVariables,
      variableTokens: this.options.variableTokens,
    }
  },

  addProseMirrorPlugins() {
    const storage = this.storage

    return [
      new Plugin({
        key: variableTokenPluginKey,
        props: {
          decorations(state) {
            const highlightUnresolvedTemplateVariables =
              storage.highlightUnresolvedTemplateVariables as boolean
            const tokens = storage.variableTokens as RichTextVariableToken[]
            if (!tokens.length && !highlightUnresolvedTemplateVariables) {
              return DecorationSet.empty
            }

            const tokensBySyntax = new Map(tokens.map((token) => [token.syntax, token]))
            const decorations: Decoration[] = []

            state.doc.descendants((node, position) => {
              if (!node.isText || !node.text) return

              for (const match of node.text.matchAll(createTemplateVariableMatcher())) {
                const syntax = match[0]
                const tokenName = matchTemplateVariableToken(syntax)
                const normalizedSyntax = tokenName ? toTemplateVariableSyntax(tokenName) : syntax
                const token = tokensBySyntax.get(syntax) ?? tokensBySyntax.get(normalizedSyntax)
                if (match.index === undefined) continue

                if (!token) {
                  if (!highlightUnresolvedTemplateVariables) continue

                  decorations.push(
                    Decoration.inline(
                      position + match.index,
                      position + match.index + syntax.length,
                      {
                        class: 'template-variable-token template-variable-token--unresolved',
                        'data-label': syntax,
                        spellcheck: 'false',
                      }
                    )
                  )
                  continue
                }

                // Take the CSS suffix from the group config rather than munging
                // the group name: lowercasing "Trigger event" yields a class
                // fragment containing a space, which the browser reads as two
                // classes (neither styled, plus a stray global `event` class).
                const parentClass = token.parentType
                  ? ` template-variable-token--${getTemplateVariableGroupConfig(token.parentType).slug}`
                  : ''

                decorations.push(
                  Decoration.inline(
                    position + match.index,
                    position + match.index + syntax.length,
                    {
                      class: `template-variable-token${parentClass}`,
                      'data-label': token.label,
                      spellcheck: 'false',
                    }
                  )
                )
              }
            })

            return DecorationSet.create(state.doc, decorations)
          },

          // Tokens only *look* atomic — they are plain text, so the default
          // Backspace would shave off the trailing `}` and leave a broken
          // half-token that re-renders as raw "unresolved" text. Delete the
          // whole token instead whenever the caret sits inside one or right
          // after it. Non-empty selections and carets outside a token fall
          // through to the default handling.
          handleKeyDown(view, event) {
            if (event.key !== 'Backspace') return false

            // Same bail as `decorations`: editors that draw no chips (plain
            // description fields, say) leave `{{ ... }}` as ordinary text, so
            // it should delete one character at a time like ordinary text.
            const tokens = storage.variableTokens as RichTextVariableToken[]
            if (!tokens.length && !storage.highlightUnresolvedTemplateVariables) return false

            const { empty, $from } = view.state.selection
            if (!empty || !$from.parent.isTextblock) return false

            const parent = $from.parent
            // The leaf placeholder keeps offsets aligned with document
            // positions: every inline leaf node counts as one character.
            const text = parent.textBetween(0, parent.content.size, undefined, '\uFFFC')
            const token = findTemplateVariableTokenAtOffset(text, $from.parentOffset)
            if (!token) return false

            const blockStart = $from.start()
            view.dispatch(view.state.tr.delete(blockStart + token.start, blockStart + token.end))
            return true
          },
        },
      }),
    ]
  },
})

export function getEditorPlainText(editor: Editor): string {
  return editor.state.doc.textBetween(0, editor.state.doc.content.size, '\n')
}

export function updateVariableTokenDecorations(
  editor: Editor,
  {
    highlightUnresolvedTemplateVariables,
    variableTokens,
  }: {
    highlightUnresolvedTemplateVariables: boolean
    variableTokens: RichTextVariableToken[]
  }
) {
  const storage = editor.storage as unknown as TemplateVariableDecorationStorage
  storage.variableTokenDecorations.highlightUnresolvedTemplateVariables =
    highlightUnresolvedTemplateVariables
  storage.variableTokenDecorations.variableTokens = variableTokens
  editor.view.dispatch(
    editor.state.tr
      .setMeta(variableTokenPluginKey, {
        highlightUnresolvedTemplateVariables,
        variableTokens,
      })
      .setMeta('addToHistory', false)
  )
}

/**
 * useRichTextEditor - Custom hook for TipTap editor setup
 *
 * Provides a consistent TipTap editor configuration with:
 * - StarterKit extensions (bold, italic, lists, etc.)
 * - Placeholder support
 * - Customizable styling
 * - Update callback
 *
 * @param options - Configuration options
 * @returns Editor instance
 */
export function useRichTextEditor({
  id,
  initialContent = '',
  placeholder = 'Enter text...',
  editable = true,
  minHeight = '120px',
  onUpdate,
  onFocus,
  onBlur,
  variableTokens = emptyVariableTokens,
  highlightUnresolvedTemplateVariables = false,
  links = false,
  restrictedFormatting = false,
}: UseRichTextEditorOptions = {}): Editor | null {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        ...(restrictedFormatting
          ? {
              heading: false,
              blockquote: false,
              codeBlock: false,
              code: false,
              horizontalRule: false,
              strike: false,
            }
          : {}),
      }),
      Placeholder.configure({
        placeholder,
      }),
      variableTokenDecorations.configure({
        highlightUnresolvedTemplateVariables,
        variableTokens,
      }),
      ...(links
        ? [
            Link.configure({
              openOnClick: false,
              autolink: true,
              linkOnPaste: true,
              HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
            }),
          ]
        : []),
    ],
    content: initialContent,
    editable,
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        class: `focus:outline-none min-h-[${minHeight}] px-3 py-2`,
      },
    },
    onUpdate: ({ editor }) => {
      onUpdate?.(editor.getHTML())
    },
    onFocus: () => {
      onFocus?.()
    },
    onBlur: () => {
      onBlur?.()
    },
  })

  useEffect(() => {
    if (!editor) return

    updateVariableTokenDecorations(editor, {
      highlightUnresolvedTemplateVariables,
      variableTokens,
    })
  }, [editor, highlightUnresolvedTemplateVariables, variableTokens])

  return editor
}
